import { useResizableColumns, ResizableTable, ResizableTh } from "@/components/ui/resizable-table";   // Step 11d · add-on: adjustable columns
import React, { useCallback, useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import api, { toErrMsg } from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";
import { useEngagement } from "@/contexts/EngagementContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { SheetEditor } from "@/pages/Vouching";
import { fmtDateTime } from "@/lib/dt";
import { methodText } from "@/pages/Sampling/evaluationFormat";   // Step 7a · S16

// Round 21 · A1 — -04 "Vouching sheet(s)" opens THIS head folder's sheets in a full-screen dialog.
// Test-ids: hf-vouch-dialog hf-vouch-list hf-vouch-row-<id> hf-vouch-empty hf-vouch-create hf-vouch-link hf-vouch-link-select hf-vouch-back
export default function HeadFolderVouchingDialog() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const { currentEngId, current } = useEngagement();
  const fs = params.get("openVouch") || "";
  const [sheets, setSheets] = useState([]);
  const [others, setOthers] = useState([]);
  const sheetCols = useResizableColumns({ storageKey: "hf-vouching-sheets", defaults: [300, 160, 90, 90, 150, 90], mins: [140, 100, 64, 64, 110, 60] });
  const [sel, setSel] = useState(null);
  const [linkId, setLinkId] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    if (!fs || !currentEngId) return;
    setLoading(true);
    try {
      const r = await api.get(`/vouching/${currentEngId}/sheet-summary`);
      const all = r.data || [];
      setSheets(all.filter((s) => (s.file_in_folder || "").toLowerCase() === fs.toLowerCase()));
      setOthers(all.filter((s) => (s.file_in_folder || "").toLowerCase() !== fs.toLowerCase()));
    } catch (e) { toast.error(toErrMsg(e, "Failed")); }
    finally { setLoading(false); }
  }, [fs, currentEngId]);
  useEffect(() => { setSel(null); load(); }, [load]);

  const close = () => { const n = new URLSearchParams(params); n.delete("openVouch"); setParams(n, { replace: true }); };
  const link = async () => {
    if (!linkId) return;
    try { await api.post(`/samples/${linkId}/link-folder`, { file_in_folder: fs }); toast.success(t("headFolders.vouch.linked")); setLinkId(""); await load(); }
    catch (e) { toast.error(toErrMsg(e, "Failed")); }
  };
  if (!fs) return null;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) close(); }}>
      <DialogContent className="max-w-[98vw] w-[98vw] h-[94vh] flex flex-col overflow-hidden" data-testid="hf-vouch-dialog">
        <DialogHeader>
          <DialogTitle className="text-base flex items-center gap-2">
            {sel && <Button size="sm" variant="ghost" className="h-6 px-2 text-xs" onClick={() => setSel(null)} data-testid="hf-vouch-back">← {t("headFolders.vouch.back")}</Button>}
            <span dir="ltr">{fs}-04</span> · {t("headFolders.vouch.title")} <span className="text-muted-foreground text-xs">({t("headFolders.vouch.filtered").replace("{fs}", fs)})</span>
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 min-h-0 overflow-auto">
          {sel ? (
            <SheetEditor engagement={current || { id: currentEngId }} sampleId={sel} onBack={() => setSel(null)} t={t} />
          ) : loading ? <p className="p-4 text-sm text-muted-foreground">…</p> : sheets.length === 0 ? (
            <div className="p-6 text-sm" data-testid="hf-vouch-empty">
              <p className="font-medium">{t("headFolders.vouch.none").replace("{fs}", fs)}</p>
              <div className="mt-3 flex flex-wrap items-center gap-2">
                <Button size="sm" onClick={() => navigate(`/sampling?fs=${encodeURIComponent(fs)}`)} data-testid="hf-vouch-create">{t("headFolders.vouch.create")}</Button>
                <select className="h-8 rounded border bg-white px-2 text-xs" value={linkId} onChange={(e) => setLinkId(e.target.value)} data-testid="hf-vouch-link-select">
                  <option value="">{t("headFolders.vouch.pickExisting")}</option>
                  {others.map((s) => <option key={s.sample_id} value={s.sample_id}>{s.name} · {methodText(s.method, t)}{s.file_in_folder ? ` · ${s.file_in_folder}` : ""}</option>)}
                </select>
                <Button size="sm" variant="outline" disabled={!linkId} onClick={link} data-testid="hf-vouch-link">{t("headFolders.vouch.link")}</Button>
              </div>
            </div>
          ) : (
            <ResizableTable cols={sheetCols} wrap className="w-full text-sm" data-testid="hf-vouch-list">
              <thead className="bg-muted/50 text-xs"><tr>
                <ResizableTh index={0} className="px-3 py-2 text-start">{t("headFolders.vouch.sheet")}</ResizableTh><ResizableTh index={1} className="px-3 py-2 text-start">{t("headFolders.vouch.method")}</ResizableTh>
                <ResizableTh index={2} className="px-3 py-2 text-end">{t("headFolders.vouch.items")}</ResizableTh><ResizableTh index={3} className="px-3 py-2 text-end">{t("headFolders.vouch.tested")}</ResizableTh>
                <ResizableTh index={4} className="px-3 py-2 text-start">{t("headFolders.vouch.updated")}</ResizableTh><ResizableTh index={5}></ResizableTh>
              </tr></thead>
              <tbody>
                {sheets.map((s) => (
                  <tr key={s.sample_id} className="border-t hover:bg-muted/30 cursor-pointer" onClick={() => setSel(s.sample_id)} data-testid={`hf-vouch-row-${s.sample_id}`}>
                    <td className="px-3 py-2"><bdi>{s.name}</bdi></td><td className="px-3 py-2">{methodText(s.method, t)}</td>
                    <td className="px-3 py-2 text-end">{s.item_count ?? s.items ?? "—"}</td><td className="px-3 py-2 text-end">{s.tested_count ?? s.tested ?? "—"}</td>
                    <td className="px-3 py-2 text-xs"><bdi>{s.updated_at ? fmtDateTime(s.updated_at) : "—"}</bdi></td>
                    <td className="px-3 py-2 text-end text-xs text-sky-700 underline">{t("headFolders.vouch.open")}</td>
                  </tr>
                ))}
              </tbody>
            </ResizableTable>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
